import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usuarioService, okrService, keyResultService } from '@/services/api';
import { cronogramaService } from '@/services/cronogramasApi';
import { projetotService } from '@/services/projetotApi';
import { calendarioService, calendarioEventoService } from '@/services/calendarioApi';
import { crmNegociosService, crmAtividadesService, crmComentarioService, crmAnexoService } from '@/services/crmApi';
import { usePollingRefresh } from '@/hooks/usePollingRefresh';
import { useAuth } from '@/contexts/AuthContext';
import { CrmNegocio, CrmNegocioMovimentacao, CrmAtividade, CrmAtividadeTipo, CrmNegocioComentario, CrmNegocioAnexo } from '@/types/crm';
import { Usuario, OKR, KeyResult, Calendario } from '@/types';
import { Cronograma } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { CrmNegocioComentarios } from '@/components/crm/CrmNegocioComentarios';
import { CrmNegocioAnexos } from '@/components/crm/CrmNegocioAnexos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Loader2, Plus, Phone, Users2, Mail, CheckSquare, Trash2, History, Building2, User,
  CalendarRange, Target, X, Link2, CalendarPlus, CalendarCheck2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseDateOnly } from '@/lib/dateOnly';
import { formatMoeda } from '@/lib/formatMoeda';

const TIPO_CONFIG: Record<CrmAtividadeTipo, { label: string; icon: typeof Phone }> = {
  ligacao: { label: 'Ligação', icon: Phone },
  reuniao: { label: 'Reunião', icon: Users2 },
  email: { label: 'E-mail', icon: Mail },
  tarefa: { label: 'Tarefa', icon: CheckSquare },
};

// Mesmo intervalo do polling do Kanban (Crm.tsx) — não precisa ser idêntico, só consistente.
const NEGOCIO_DETALHE_POLL_MS = 8000;

const EMPTY_FORM = {
  tipo: 'tarefa' as CrmAtividadeTipo,
  titulo: '',
  descricao: '',
  data_prevista: '',
  responsavel_id: '',
};


export default function CrmNegocioDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [negocio, setNegocio] = useState<CrmNegocio | null>(null);
  const [atividades, setAtividades] = useState<CrmAtividade[]>([]);
  const [movimentacoes, setMovimentacoes] = useState<CrmNegocioMovimentacao[]>([]);
  const [comentarios, setComentarios] = useState<CrmNegocioComentario[]>([]);
  const [anexos, setAnexos] = useState<CrmNegocioAnexo[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Integração com Calendário (etapa P1). `calendarioDisponivel` distingue "módulo
  // desabilitado pra essa empresa" (UI de vínculo nem aparece) de "módulo habilitado, mas
  // sem calendário editável" (mostra mensagem explicativa, decisão combinada com o usuário).
  const [calendarios, setCalendarios] = useState<Calendario[]>([]);
  const [calendarioDisponivel, setCalendarioDisponivel] = useState(false);
  const [vinculandoCalendarioId, setVinculandoCalendarioId] = useState<string | null>(null);
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const calendariosEditaveis = isAdmin
    ? calendarios
    : calendarios.filter((cal) => cal.acessos?.some((acesso) => acesso.pode_editar));

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Integrações (etapa 6): Cronograma e OKR/Key Result vinculados ao negócio.
  const [cronogramas, setCronogramas] = useState<Cronograma[]>([]);
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [isCronogramaDialogOpen, setIsCronogramaDialogOpen] = useState(false);
  const [cronogramaSelecionado, setCronogramaSelecionado] = useState('');
  const [isMetaDialogOpen, setIsMetaDialogOpen] = useState(false);
  const [metaSelecionada, setMetaSelecionada] = useState(''); // "okr:<id>" ou "kr:<id>"

  // Cada chamada de loadData ganha um número de geração — se uma rodada mais recente já
  // resolveu quando esta (mais antiga, ex: poll lento) termina, a resposta desatualizada é
  // descartada em vez de sobrescrever o estado já atualizado por dados mais novos.
  const loadDataGenerationRef = useRef(0);

  // silent=true (usado pelo polling em segundo plano) pula o spinner de página inteira —
  // sem isso, a tela inteira "piscaria" pro loading a cada ciclo do poll.
  const loadData = async (silent = false) => {
    if (!id) return;
    const generation = ++loadDataGenerationRef.current;
    try {
      if (!silent) setIsLoading(true);
      const [negocioRes, atividadesRes, movRes, comentariosRes, anexosRes] = await Promise.all([
        crmNegociosService.getById(id),
        crmAtividadesService.getAll({ negocio_id: id }),
        crmNegociosService.getMovimentacoes(id),
        crmComentarioService.getAll(id),
        crmAnexoService.list(id),
      ]);
      if (generation !== loadDataGenerationRef.current) return; // uma chamada mais nova já resolveu
      if (negocioRes.success) setNegocio(negocioRes.data);
      if (atividadesRes.success) setAtividades(atividadesRes.data);
      if (movRes.success) setMovimentacoes(movRes.data);
      if (comentariosRes.success) setComentarios(comentariosRes.data);
      if (anexosRes.success) setAnexos(anexosRes.data);
    } catch (error) {
      console.error('Error loading negocio detalhe:', error);
      if (!silent) toast.error('Erro ao carregar negócio');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  usePollingRefresh(() => loadData(true), NEGOCIO_DETALHE_POLL_MS, !!id);

  useEffect(() => {
    if (negocio?.empresa_id) {
      usuarioService.getByEmpresa(negocio.empresa_id).then((res) => {
        if (res.success) setUsuarios(res.data);
      });
    }
    projetotService.getAll(undefined, negocio?.empresa_id).then((res) => {
      if (res.success) setCronogramas(res.data);
    });
    if (negocio?.empresa_id) {
      okrService.getByEmpresa(negocio.empresa_id).then((res) => {
        if (res.success) setOkrs(res.data);
      });
    }
    keyResultService.getAll().then((res) => {
      if (res.success) setKeyResults(res.data);
    });
    if (negocio?.empresa_id) {
      // Se o módulo Calendário não estiver habilitado pra essa empresa, a API retorna
      // success:false (403 de requireModule) — usado pra esconder a UI de vínculo por
      // completo, diferente do caso "habilitado, mas sem calendário editável".
      calendarioService.getAll(negocio.empresa_id).then((res) => {
        setCalendarioDisponivel(res.success);
        setCalendarios(res.success ? res.data : []);
      });
    }
  }, [negocio?.empresa_id]);

  const openNewAtividade = () => {
    setForm(EMPTY_FORM);
    setIsDialogOpen(true);
  };

  const handleCreateAtividade = async () => {
    if (!id || !negocio) return;
    try {
      const result = await crmAtividadesService.create({
        negocio_id: id,
        tipo: form.tipo,
        titulo: form.titulo,
        descricao: form.descricao || null,
        data_prevista: form.data_prevista,
        responsavel_id: form.responsavel_id || undefined,
        empresa_id: negocio.empresa_id,
      });
      if (result.success) {
        toast.success('Atividade registrada');
        loadData();
        setIsDialogOpen(false);
      } else {
        toast.error(result.error || 'Erro ao registrar atividade');
      }
    } catch (error) {
      toast.error('Erro ao registrar atividade');
    }
  };

  const handleToggleConcluida = async (atividade: CrmAtividade) => {
    try {
      const result = await crmAtividadesService.update(atividade.id, { concluida: !atividade.concluida });
      if (result.success) {
        loadData();
      } else {
        toast.error(result.error || 'Erro ao atualizar atividade');
      }
    } catch (error) {
      toast.error('Erro ao atualizar atividade');
    }
  };

  const handleAdicionarAoCalendario = async (atividade: CrmAtividade) => {
    if (calendariosEditaveis.length === 0) {
      toast.error('Você não tem nenhum calendário editável nesta empresa', {
        action: { label: 'Ir para Calendário', onClick: () => navigate('/calendario') },
      });
      return;
    }
    setVinculandoCalendarioId(atividade.id);
    try {
      const evento = await calendarioEventoService.create(calendariosEditaveis[0].id, {
        titulo: atividade.titulo,
        descricao: atividade.descricao || undefined,
        data_inicio: `${atividade.data_prevista}T00:00:00`,
        dia_inteiro: true,
        categoria: TIPO_CONFIG[atividade.tipo].label,
      });
      if (!evento.success || !evento.data) {
        toast.error(evento.error || 'Erro ao criar evento no calendário');
        return;
      }
      const vinculo = await crmAtividadesService.update(atividade.id, { calendario_evento_id: evento.data.id });
      if (vinculo.success) {
        toast.success('Adicionado ao Calendário');
        loadData();
      } else {
        toast.error(vinculo.error || 'Evento criado, mas não foi possível vincular');
      }
    } catch (error) {
      toast.error('Erro ao adicionar ao Calendário');
    } finally {
      setVinculandoCalendarioId(null);
    }
  };

  const handleRemoverDoCalendario = async (atividade: CrmAtividade) => {
    setVinculandoCalendarioId(atividade.id);
    try {
      const result = await crmAtividadesService.update(atividade.id, { calendario_evento_id: null });
      if (result.success) {
        toast.success('Removido do Calendário');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao remover do Calendário');
      }
    } catch (error) {
      toast.error('Erro ao remover do Calendário');
    } finally {
      setVinculandoCalendarioId(null);
    }
  };

  const handleDeleteAtividade = async (atividadeId: string) => {
    try {
      const result = await crmAtividadesService.delete(atividadeId);
      if (result.success) {
        toast.success('Atividade excluída');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao excluir atividade');
      }
    } catch (error) {
      toast.error('Erro ao excluir atividade');
    }
  };

  const handleCriarCronograma = async () => {
    if (!id || !negocio) return;
    try {
      const criado = await cronogramaService.create({
        titulo: negocio.titulo,
        area: 'Comercial',
        empresa_id: negocio.empresa_id,
      });
      if (!criado.success || !criado.data) {
        toast.error(criado.error || 'Erro ao criar projeto');
        return;
      }
      const vinculo = await crmNegociosService.vincularCronograma(id, criado.data.id);
      if (vinculo.success) {
        toast.success('Projeto criado e vinculado');
        loadData();
      } else {
        toast.error(vinculo.error || 'Projeto criado, mas não foi possível vincular');
      }
    } catch (error) {
      toast.error('Erro ao criar projeto');
    }
  };

  const handleVincularCronogramaExistente = async () => {
    if (!id || !cronogramaSelecionado) return;
    try {
      const result = await crmNegociosService.vincularCronograma(id, cronogramaSelecionado);
      if (result.success) {
        toast.success('Projeto vinculado');
        loadData();
        setIsCronogramaDialogOpen(false);
        setCronogramaSelecionado('');
      } else {
        toast.error(result.error || 'Erro ao vincular projeto');
      }
    } catch (error) {
      toast.error('Erro ao vincular projeto');
    }
  };

  const handleDesvincularCronograma = async () => {
    if (!id) return;
    try {
      const result = await crmNegociosService.vincularCronograma(id, null);
      if (result.success) {
        toast.success('Projeto desvinculado');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao desvincular projeto');
      }
    } catch (error) {
      toast.error('Erro ao desvincular projeto');
    }
  };

  const handleVincularMeta = async () => {
    if (!id || !metaSelecionada) return;
    const [tipo, metaId] = metaSelecionada.split(':');
    try {
      const result = await crmNegociosService.vincularOkr(id, {
        okr_id: tipo === 'okr' ? metaId : null,
        key_result_id: tipo === 'kr' ? metaId : null,
      });
      if (result.success) {
        toast.success('Meta vinculada');
        loadData();
        setIsMetaDialogOpen(false);
        setMetaSelecionada('');
      } else {
        toast.error(result.error || 'Erro ao vincular meta');
      }
    } catch (error) {
      toast.error('Erro ao vincular meta');
    }
  };

  const handleDesvincularMeta = async () => {
    if (!id) return;
    try {
      const result = await crmNegociosService.vincularOkr(id, { okr_id: null, key_result_id: null });
      if (result.success) {
        toast.success('Meta desvinculada');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao desvincular meta');
      }
    } catch (error) {
      toast.error('Erro ao desvincular meta');
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!negocio) {
    return (
      <AppLayout>
        <p className="text-muted-foreground">Negócio não encontrado.</p>
      </AppLayout>
    );
  }

  const valorFormatado = formatMoeda(negocio.valor);

  return (
    <AppLayout>
      <div className="animate-fade-in space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm')} className="-ml-2">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar ao Kanban
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{negocio.titulo}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            {valorFormatado && (
              <div>
                <p className="text-muted-foreground">Valor</p>
                <p className="font-semibold text-primary">{valorFormatado}</p>
              </div>
            )}
            {negocio.conta && (
              <div>
                <p className="text-muted-foreground">Conta</p>
                <p className="flex items-center gap-1 font-medium">
                  <Building2 className="h-3.5 w-3.5" /> {negocio.conta.razao_social}
                </p>
              </div>
            )}
            {negocio.contato && (
              <div>
                <p className="text-muted-foreground">Contato</p>
                <p className="font-medium">{negocio.contato.nome}</p>
              </div>
            )}
            {negocio.responsaveis && negocio.responsaveis.length > 0 && (
              <div>
                <p className="text-muted-foreground">{negocio.responsaveis.length > 1 ? 'Responsáveis' : 'Responsável'}</p>
                {negocio.responsaveis.map((responsavel) => (
                  <p key={responsavel.id} className="flex items-center gap-1 font-medium">
                    <User className="h-3.5 w-3.5" /> {responsavel.nome}
                  </p>
                ))}
              </div>
            )}
            {negocio.data_previsao_fechamento && (
              <div>
                <p className="text-muted-foreground">Previsão de fechamento</p>
                <p className="font-medium">{format(parseDateOnly(negocio.data_previsao_fechamento), 'dd/MM/yyyy')}</p>
              </div>
            )}
            {negocio.data_fechamento && (
              <div>
                <p className="text-muted-foreground">Fechado em</p>
                <p className="font-medium">{format(parseDateOnly(negocio.data_fechamento), 'dd/MM/yyyy')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Atividades</CardTitle>
              <Button size="sm" onClick={openNewAtividade}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Atividade
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {atividades.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhuma atividade registrada</p>
              )}
              {atividades.map((atividade) => {
                const config = TIPO_CONFIG[atividade.tipo];
                const Icon = config.icon;
                return (
                  <div
                    key={atividade.id}
                    className="flex items-start gap-3 rounded-md border p-3"
                  >
                    <Checkbox
                      checked={atividade.concluida}
                      onCheckedChange={() => handleToggleConcluida(atividade)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className={`text-sm font-medium ${atividade.concluida ? 'line-through text-muted-foreground' : ''}`}>
                          {atividade.titulo}
                        </span>
                        <Badge variant="outline" className="text-xs">{config.label}</Badge>
                      </div>
                      {atividade.descricao && (
                        <p className="mt-1 text-xs text-muted-foreground">{atividade.descricao}</p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        Previsto para {format(parseDateOnly(atividade.data_prevista), 'dd/MM/yyyy')}
                        {atividade.responsavel && ` · ${atividade.responsavel.nome}`}
                      </p>
                    </div>
                    {calendarioDisponivel && (
                      atividade.calendario_evento_id ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-primary"
                          title="No Calendário — clique para remover"
                          disabled={vinculandoCalendarioId === atividade.id}
                          onClick={() => handleRemoverDoCalendario(atividade)}
                        >
                          <CalendarCheck2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Adicionar ao Calendário"
                          disabled={vinculandoCalendarioId === atividade.id}
                          onClick={() => handleAdicionarAoCalendario(atividade)}
                        >
                          <CalendarPlus className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      )
                    )}
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDeleteAtividade(atividade.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4" />
                Histórico de movimentação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {movimentacoes.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhuma movimentação ainda</p>
              )}
              {movimentacoes.map((mov) => (
                <div key={mov.id} className="rounded-md border p-3 text-sm">
                  <p>
                    {mov.colunaOrigem ? mov.colunaOrigem.nome : '—'}
                    {' → '}
                    <span className="font-medium">{mov.colunaDestino?.nome}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {mov.usuario?.nome} · {mov.createdAt && format(new Date(mov.createdAt), 'dd/MM/yyyy HH:mm')}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <CrmNegocioComentarios negocioId={id!} comentarios={comentarios} onComentariosChange={setComentarios} />
          <CrmNegocioAnexos negocioId={id!} anexos={anexos} onAnexosChange={setAnexos} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Link2 className="h-4 w-4" />
              Integrações
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-md border p-3">
              <p className="mb-2 flex items-center gap-2 text-sm font-medium">
                <CalendarRange className="h-4 w-4" /> Projeto de execução
              </p>
              {negocio.cronograma ? (
                <div className="flex items-center justify-between">
                  <Link to={`/projetos/${negocio.cronograma.id}`} className="text-sm text-primary hover:underline">
                    {negocio.cronograma.titulo}
                  </Link>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleDesvincularCronograma} title="Desvincular">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={handleCriarCronograma}>
                    Criar Projeto
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setIsCronogramaDialogOpen(true)}>
                    Vincular existente
                  </Button>
                </div>
              )}
            </div>

            <div className="rounded-md border p-3">
              <p className="mb-2 flex items-center gap-2 text-sm font-medium">
                <Target className="h-4 w-4" /> Meta (OKR/Key Result)
              </p>
              {negocio.okrRelacionado || negocio.keyResultRelacionado ? (
                <div className="flex items-center justify-between">
                  <span className="text-sm">
                    {negocio.okrRelacionado?.titulo || negocio.keyResultRelacionado?.descricao}
                  </span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleDesvincularMeta} title="Desvincular">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setIsMetaDialogOpen(true)}>
                  Vincular a uma meta
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Atividade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ativ_tipo">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v as CrmAtividadeTipo }))}>
                <SelectTrigger id="ativ_tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_CONFIG).map(([value, cfg]) => (
                    <SelectItem key={value} value={value}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ativ_titulo">Título *</Label>
              <Input
                id="ativ_titulo"
                value={form.titulo}
                onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex: Ligar para confirmar proposta"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ativ_descricao">Descrição</Label>
              <Textarea
                id="ativ_descricao"
                value={form.descricao}
                onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ativ_data">Data prevista *</Label>
                <Input
                  id="ativ_data"
                  type="date"
                  value={form.data_prevista}
                  onChange={(e) => setForm((f) => ({ ...f, data_prevista: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ativ_responsavel">Responsável</Label>
                <Select
                  value={form.responsavel_id || '__me__'}
                  onValueChange={(v) => setForm((f) => ({ ...f, responsavel_id: v === '__me__' ? '' : v }))}
                >
                  <SelectTrigger id="ativ_responsavel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__me__">Eu mesmo</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateAtividade} disabled={!form.titulo || !form.data_prevista}>
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCronogramaDialogOpen} onOpenChange={setIsCronogramaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular a um Projeto existente</DialogTitle>
          </DialogHeader>
          <Select value={cronogramaSelecionado} onValueChange={setCronogramaSelecionado}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione um projeto" />
            </SelectTrigger>
            <SelectContent>
              {cronogramas.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.titulo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCronogramaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleVincularCronogramaExistente} disabled={!cronogramaSelecionado}>
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isMetaDialogOpen} onOpenChange={setIsMetaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular a uma meta</DialogTitle>
          </DialogHeader>
          <Select value={metaSelecionada} onValueChange={setMetaSelecionada}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione um OKR ou Key Result" />
            </SelectTrigger>
            <SelectContent>
              {okrs.map((okr) => (
                <SelectItem key={`okr:${okr.id}`} value={`okr:${okr.id}`}>
                  🎯 {okr.titulo}
                </SelectItem>
              ))}
              {keyResults.map((kr) => (
                <SelectItem key={`kr:${kr.id}`} value={`kr:${kr.id}`}>
                  ↳ {kr.descricao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMetaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleVincularMeta} disabled={!metaSelecionada}>
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
